// Meta (Facebook) Pixel — loaded only when the server renders the
// `meta-pixel-id` meta tag (gated on ENV["META_PIXEL_ID"]). Mirrors how
// PostHog is bootstrapped (meta tag + JS init) so no inline <script> is
// needed, keeping the Content Security Policy clean. The fbevents.js loader
// injects an external https script, which script-src :https allows.

interface MetaPixelFunction {
  (command: 'init', pixelId: string): void
  (command: 'track', eventName: 'PageView' | 'CompleteRegistration'): void
}

declare global {
  interface Window {
    fbq: MetaPixelFunction
  }
}

let initialized = false

export function initMetaPixel() {
  if (initialized) return
  const pixelId = document.querySelector('meta[name="meta-pixel-id"]')?.getAttribute('content')
  if (!pixelId) return
  // Don't send events from local dev even if the id is set.
  if (import.meta.env?.DEV) return
  // Marketing/advertising cookies require consent. The pixel stays off until
  // the user accepts all cookies; the cookie banner calls this again on accept.
  if (localStorage.getItem('cookie_consent') !== 'all') return

  // Standard Meta Pixel loader (as bundled JS, not an inline page script).
  /* eslint-disable */
  ;(function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) }
    if (!f._fbq) f._fbq = n
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = []
    t = b.createElement(e); t.async = true; t.src = v
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  /* eslint-enable */

  const fbq = window.fbq
  fbq('init', pixelId)
  fbq('track', 'PageView')
  initialized = true

  // A registration that happened before the pixel could run (e.g. consent
  // granted only after signing up) is parked in sessionStorage — fire it now.
  if (sessionStorage.getItem('fb_reg_pending')) {
    sessionStorage.removeItem('fb_reg_pending')
    fbq('track', 'CompleteRegistration')
  }
}

// Fires the signup conversion exactly once. Called when an Inertia page's
// shared props carry the one-shot fbTrackRegistration flag. If the pixel
// isn't running yet (no cookie consent at that moment), the event is parked
// and initMetaPixel() sends it right after consent.
export function trackRegistration() {
  if (initialized) {
    window.fbq('track', 'CompleteRegistration')
  } else {
    sessionStorage.setItem('fb_reg_pending', '1')
  }
}
